import { ParsedUrlQuery } from 'querystring';

import { Dispatch, SetStateAction } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Checkbox } from '../../../design-system/components/form/checkbox/Checkbox';
import { Typography } from '../../../design-system/components/typography/Typography';
import { ItinerariesListAvailableFilters } from '../../../resources/itinerary/domain/types/Itinerary';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import style from './Checkboxes.module.css';

type CheckboxesFiltersProps = {
    availableFilters: PickDomain<ItinerariesListAvailableFilters, 'filters'>['filters'];
    localFilters: ParsedUrlQuery;
    setLocalFilters: Dispatch<SetStateAction<ParsedUrlQuery>>;
};

const isArray = <T,>(value: T | readonly T[]): value is readonly T[] => Array.isArray(value);

export const CheckboxesFilters = ({
    availableFilters,
    localFilters,
    setLocalFilters,
}: CheckboxesFiltersProps) => {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    function handleCheckboxChange(filterName: string, choiceName: string, isChecked: boolean) {
        const value = localFilters[filterName] ?? [];
        const currentChoices = isArray(value) ? value : [value];

        const updatedChoices = isChecked
            ? [...currentChoices, choiceName]
            : currentChoices.filter((item) => {
                  return item !== choiceName;
              });

        setLocalFilters((prevFilters) => {
            const newFilters = {
                ...prevFilters,
                [filterName]: updatedChoices,
            };

            if (updatedChoices.length === 0) {
                delete newFilters[filterName];
            }

            return newFilters;
        });

        if (isChecked) {
            trackEvent((ampli, defaultProperties) => {
                ampli.listItinerariesFilterSelected({
                    ...defaultProperties,
                    filter_name: filterName,
                    filter_value: choiceName,
                });
            });
        }
    }

    return availableFilters.map((filter) => (
        <fieldset className={style.filterFieldset} key={filter.name}>
            <Typography
                as="legend"
                scale="primary-body-bold"
                className={style.filterFieldsetLegend}
            >
                {formatMessage({
                    id: `page.itinerariesList.filters.${filter.name}` as MessageDescriptor['id'],
                })}
            </Typography>

            {filter.choices.map((choice) => {
                const isChecked = isArray(localFilters[filter.name])
                    ? (localFilters[filter.name] as string[]).includes(choice.name)
                    : localFilters[filter.name] === choice.name;

                const label =
                    choice.translation ||
                    formatMessage({
                        id: `page.itinerariesList.filters.${choice.name}` as MessageDescriptor['id'],
                    });

                return (
                    <Checkbox
                        key={choice.name}
                        label={label}
                        checkboxProps={{
                            name: choice.name,
                            value: choice.name,
                            checked: isChecked,
                            onChange: (event) => {
                                handleCheckboxChange(
                                    filter.name,
                                    choice.name,
                                    event.target.checked,
                                );
                            },
                        }}
                    />
                );
            })}
        </fieldset>
    ));
};
